import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiFileText, FiCheck, FiAlertCircle, FiDownload, FiMail, FiLock } from 'react-icons/fi';
import SignatureCanvas from './SignatureCanvas';
import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';

const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;

const PurchaseAgreement = ({ isOpen, onClose, onComplete, shed, dealer, customer: initialCustomer, paymentType = 'deposit' }) => {
  const [step, setStep] = useState(0); // 0=info, 1=review, 2=sign, 3=confirmed
  const [signature, setSignature] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const agreementRef = useRef(null);
  const [customer, setCustomer] = useState(initialCustomer || { name: '', email: '', phone: '' });

  const isDeposit = paymentType === 'deposit';
  const shedPrice = shed?.salePrice || shed?.price || 0;
  const depositAmount = shed?.depositAmount || Math.round(shedPrice * 0.25);
  const amount = isDeposit ? depositAmount : shedPrice;
  const balanceDue = isDeposit ? shedPrice - depositAmount : 0;
  const today = new Date();
  const invNum = invoiceNumber || `INV-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${Math.random().toString(36).substring(2, 7).toUpperCase()}`;

  if (!invoiceNumber) setInvoiceNumber(invNum);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(price);
  };

  const handleSign = async () => {
    if (!signature) {
      setError('Please sign the agreement to continue');
      return;
    }
    if (!agreed) {
      setError('Please accept the terms to continue');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save agreement to Supabase
      const agreementData = {
        invoice_number: invNum,
        dealer_id: dealer?.id || null,
        shed_id: shed?.id || null,
        customer_name: customer?.name || '',
        customer_email: customer?.email || '',
        customer_phone: customer?.phone || '',
        shed_details: {
          name: shed?.name,
          size: shed?.size,
          shedType: shed?.shedType,
          material: shed?.material,
          bodyColor: shed?.bodyColor,
          trimColor: shed?.trimColor,
          roofColor: shed?.roofColor,
          roofStyle: shed?.roofStyle,
          doorType: shed?.doorType,
          windowType: shed?.windowType,
          features: shed?.features || [],
          serialNumber: shed?.serialNumber,
          stockNumber: shed?.stockNumber,
          warranty: shed?.warranty
        },
        payment_type: paymentType,
        amount: amount,
        total_price: shedPrice,
        balance_due: balanceDue,
        signature_data: signature,
        signed_at: new Date().toISOString(),
        status: 'signed',
        dealer_company: dealer?.company_name || ''
      };

      // Try saving to Supabase (table may not exist yet)
      try {
        await supabase.from('purchase_agreements').insert([agreementData]);
      } catch (dbErr) {
        console.warn('Could not save agreement to DB (table may not exist):', dbErr);
      }

      // Send emails
      await sendEmails(agreementData);

      setStep(3);
    } catch (err) {
      console.error('Error completing agreement:', err);
      setError('Failed to process agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendEmails = async (agreementData) => {
    const invoiceHtml = buildInvoiceEmailHtml(agreementData);

    // Send to customer
    if (customer?.email) {
      await sendEmail(customer.email, agreementData, invoiceHtml, 'customer');
    }

    // Send to dealer
    if (dealer?.email) {
      await sendEmail(dealer.email, agreementData, invoiceHtml, 'dealer');
    }
  };

  const sendEmail = async (toEmail, agreementData, invoiceHtml, recipientType) => {
    // Try Supabase Edge Function first
    try {
      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: toEmail,
          subject: `${recipientType === 'dealer' ? '[New Order] ' : ''}Shed Purchase Agreement — ${agreementData.invoice_number}`,
          html: invoiceHtml
        }
      });
      if (!error) return;
    } catch (e) {
      console.warn('Supabase email unavailable, trying EmailJS');
    }

    // Fallback to EmailJS
    if (EMAILJS_PUBLIC_KEY && EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID) {
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
          to_email: toEmail,
          from_name: 'ShedX',
          subject: `Shed Purchase Agreement — ${agreementData.invoice_number}`,
          message: invoiceHtml
        });
      } catch (ejsErr) {
        console.error(`EmailJS failed for ${recipientType}:`, ejsErr);
      }
    }
  };

  const buildInvoiceEmailHtml = (data) => {
    const features = data.shed_details?.features || [];
    return `
      <div style="font-family:system-ui,-apple-system,sans-serif;max-width:640px;margin:0 auto;background:#fff;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
        <div style="background:#111827;color:#fff;padding:24px 32px;">
          <h1 style="margin:0;font-size:20px;font-weight:600;">ShedX Purchase Agreement</h1>
          <p style="margin:4px 0 0;font-size:13px;color:#9ca3af;">Invoice ${data.invoice_number}</p>
        </div>
        <div style="padding:24px 32px;">
          <table style="width:100%;border-collapse:collapse;font-size:14px;">
            <tr><td style="padding:8px 0;color:#6b7280;">Date</td><td style="padding:8px 0;text-align:right;font-weight:500;">${new Date(data.signed_at).toLocaleDateString()}</td></tr>
            <tr><td style="padding:8px 0;color:#6b7280;">Customer</td><td style="padding:8px 0;text-align:right;font-weight:500;">${data.customer_name}</td></tr>
            <tr><td style="padding:8px 0;color:#6b7280;">Email</td><td style="padding:8px 0;text-align:right;">${data.customer_email}</td></tr>
            ${data.customer_phone ? `<tr><td style="padding:8px 0;color:#6b7280;">Phone</td><td style="padding:8px 0;text-align:right;">${data.customer_phone}</td></tr>` : ''}
            ${data.dealer_company ? `<tr><td style="padding:8px 0;color:#6b7280;">Dealer</td><td style="padding:8px 0;text-align:right;font-weight:500;">${data.dealer_company}</td></tr>` : ''}
          </table>

          <hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />
          <h2 style="font-size:15px;font-weight:600;margin:0 0 12px;">Shed Details</h2>
          <table style="width:100%;border-collapse:collapse;font-size:13px;">
            <tr><td style="padding:6px 0;color:#6b7280;">Model</td><td style="padding:6px 0;text-align:right;">${data.shed_details.name || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Size</td><td style="padding:6px 0;text-align:right;">${data.shed_details.size || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Type</td><td style="padding:6px 0;text-align:right;">${data.shed_details.shedType || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Material</td><td style="padding:6px 0;text-align:right;">${data.shed_details.material || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Body Color</td><td style="padding:6px 0;text-align:right;">${data.shed_details.bodyColor || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Trim Color</td><td style="padding:6px 0;text-align:right;">${data.shed_details.trimColor || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Roof</td><td style="padding:6px 0;text-align:right;">${data.shed_details.roofStyle || ''} — ${data.shed_details.roofColor || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Doors</td><td style="padding:6px 0;text-align:right;">${data.shed_details.doorType?.replace(/_/g, ' ') || ''}</td></tr>
            <tr><td style="padding:6px 0;color:#6b7280;">Windows</td><td style="padding:6px 0;text-align:right;">${data.shed_details.windowType?.replace(/_/g, ' ') || ''}</td></tr>
            ${data.shed_details.serialNumber ? `<tr><td style="padding:6px 0;color:#6b7280;">Serial #</td><td style="padding:6px 0;text-align:right;">${data.shed_details.serialNumber}</td></tr>` : ''}
            ${data.shed_details.warranty ? `<tr><td style="padding:6px 0;color:#6b7280;">Warranty</td><td style="padding:6px 0;text-align:right;">${data.shed_details.warranty}</td></tr>` : ''}
          </table>

          ${features.length > 0 ? `
            <div style="margin-top:12px;">
              <p style="font-size:13px;font-weight:600;margin:0 0 6px;">Features Included</p>
              <ul style="margin:0;padding:0 0 0 18px;font-size:13px;color:#374151;">
                ${features.map(f => `<li style="padding:2px 0;">${f}</li>`).join('')}
              </ul>
            </div>
          ` : ''}

          <hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />
          <h2 style="font-size:15px;font-weight:600;margin:0 0 12px;">Payment Summary</h2>
          <table style="width:100%;border-collapse:collapse;font-size:14px;">
            <tr><td style="padding:6px 0;color:#6b7280;">Shed Price</td><td style="padding:6px 0;text-align:right;">$${Number(data.total_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td></tr>
            <tr style="font-weight:600;${data.payment_type === 'deposit' ? '' : 'font-size:16px;'}">
              <td style="padding:8px 0;">${data.payment_type === 'deposit' ? 'Deposit Paid' : 'Total Paid'}</td>
              <td style="padding:8px 0;text-align:right;">$${Number(data.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
            </tr>
            ${data.balance_due > 0 ? `<tr><td style="padding:6px 0;color:#6b7280;">Balance Due at Delivery</td><td style="padding:6px 0;text-align:right;color:#dc2626;font-weight:500;">$${Number(data.balance_due).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td></tr>` : ''}
          </table>

          <div style="margin-top:20px;padding:16px;background:#f0fdf4;border-radius:6px;text-align:center;">
            <p style="margin:0;font-size:13px;color:#15803d;font-weight:500;">✓ Digitally signed on ${new Date(data.signed_at).toLocaleString()}</p>
          </div>

          <p style="margin:20px 0 0;font-size:11px;color:#9ca3af;line-height:1.5;">
            By signing this agreement, the buyer confirms they have reviewed all shed details listed above and agrees to purchase the specified shed under the stated terms. 
            Deposits are non-refundable and will be applied toward the total purchase price. 
            Delivery and setup details will be coordinated by the dealer.
          </p>
        </div>
        <div style="background:#f9fafb;padding:16px 32px;text-align:center;font-size:11px;color:#9ca3af;">
          ShedX — Connecting buyers with quality sheds from trusted dealers
        </div>
      </div>
    `;
  };

  const handleProceedToPayment = () => {
    if (onComplete) {
      onComplete({
        invoiceNumber: invNum,
        signature,
        agreedAt: new Date().toISOString(),
        name: customer?.name || '',
        email: customer?.email || '',
        phone: customer?.phone || ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-900 text-white">
            <div className="flex items-center gap-2">
              <FiFileText className="w-5 h-5" />
              <h2 className="text-lg font-semibold">
                {step === 0 && 'Your Information'}
                {step === 1 && 'Review Purchase Agreement'}
                {step === 2 && 'Sign Agreement'}
                {step === 3 && 'Agreement Confirmed'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <div ref={agreementRef} className="max-h-[70vh] overflow-y-auto">
            {/* Step 0: Customer Info */}
            {step === 0 && (
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-5">Enter your details to generate the purchase agreement.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">A copy of the signed agreement will be emailed here</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={customer.phone}
                      onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                {/* Shed quick summary */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4 flex gap-4">
                  {shed?.images?.[0] && (
                    <img src={shed.images[0]} alt={shed.name} className="w-20 h-20 rounded-lg object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{shed?.name}</p>
                    <p className="text-xs text-gray-500">{shed?.size} &middot; {shed?.shedType}</p>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{formatPrice(amount)} {isDeposit ? 'deposit' : 'total'}</p>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <FiAlertCircle className="flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  onClick={() => {
                    if (!customer.name || !customer.email) {
                      setError('Name and email are required');
                      return;
                    }
                    setError('');
                    setStep(1);
                  }}
                  className="w-full mt-6 bg-gray-900 hover:bg-gray-800 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FiFileText className="w-4 h-4" />
                  Review Agreement
                </button>
              </div>
            )}

            {/* Step 1: Review */}
            {step === 1 && (
              <div className="p-6">
                {/* Invoice Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Invoice</p>
                    <p className="font-mono text-sm font-medium text-gray-900">{invNum}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Date</p>
                    <p className="text-sm text-gray-900">{today.toLocaleDateString()}</p>
                  </div>
                </div>

                {/* Customer & Dealer Info */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Buyer</p>
                    <p className="text-sm font-medium text-gray-900">{customer?.name}</p>
                    <p className="text-xs text-gray-500">{customer?.email}</p>
                    {customer?.phone && <p className="text-xs text-gray-500">{customer?.phone}</p>}
                  </div>
                  {dealer?.company_name && (
                    <div className="text-right">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Seller</p>
                      <p className="text-sm font-medium text-gray-900">{dealer.company_name}</p>
                    </div>
                  )}
                </div>

                {/* Shed Details - Itemized */}
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                  <div className="bg-gray-50 px-4 py-2.5">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Shed Specifications</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {[
                      ['Model', shed?.name],
                      ['Size', shed?.size],
                      ['Type', shed?.shedType],
                      ['Material', shed?.material],
                      ['Body Color', shed?.bodyColor],
                      ['Trim Color', shed?.trimColor],
                      ['Roof Style', shed?.roofStyle],
                      ['Roof Color', shed?.roofColor],
                      ['Door Type', shed?.doorType?.replace(/_/g, ' ')],
                      ['Windows', shed?.windowType?.replace(/_/g, ' ')],
                      ['Floor', shed?.floorType],
                      ['Foundation', shed?.foundation],
                      ['Serial #', shed?.serialNumber],
                      ['Stock #', shed?.stockNumber],
                      ['Year Built', shed?.yearBuilt],
                      ['Warranty', shed?.warranty],
                      ['Condition', shed?.condition],
                    ].filter(([, val]) => val).map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-gray-500">{label}</span>
                        <span className="font-medium text-gray-900 capitalize">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                {shed?.features?.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                    <div className="bg-gray-50 px-4 py-2.5">
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Features Included</h3>
                    </div>
                    <div className="p-4">
                      <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                        {shed.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                            <FiCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Extras */}
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                  <div className="bg-gray-50 px-4 py-2.5">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Included Services</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {[
                      ['Delivery', shed?.deliveryIncluded ? 'Included' : 'Not included'],
                      ['Setup', shed?.setupIncluded ? 'Included' : 'Not included'],
                      ['Loft', shed?.loft ? 'Yes' : 'No'],
                      ['Electrical', shed?.electrical ? 'Yes' : 'No'],
                      ['Insulation', shed?.insulation ? 'Yes' : 'No'],
                    ].map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-gray-500">{label}</span>
                        <span className={`font-medium ${value === 'Included' || value === 'Yes' ? 'text-green-600' : 'text-gray-400'}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                  <div className="bg-gray-50 px-4 py-2.5">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment Summary</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    <div className="flex items-center justify-between px-4 py-3 text-sm">
                      <span className="text-gray-500">Shed Price</span>
                      <span className="font-medium text-gray-900">{formatPrice(shedPrice)}</span>
                    </div>
                    {isDeposit && (
                      <>
                        <div className="flex items-center justify-between px-4 py-3 text-sm bg-emerald-50">
                          <span className="font-medium text-emerald-700">Deposit Due Now</span>
                          <span className="font-semibold text-emerald-700 text-base">{formatPrice(depositAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 text-sm">
                          <span className="text-gray-500">Balance Due at Delivery</span>
                          <span className="font-medium text-gray-900">{formatPrice(balanceDue)}</span>
                        </div>
                      </>
                    )}
                    {!isDeposit && (
                      <div className="flex items-center justify-between px-4 py-3 text-sm bg-emerald-50">
                        <span className="font-medium text-emerald-700">Total Due</span>
                        <span className="font-semibold text-emerald-700 text-base">{formatPrice(shedPrice)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Terms */}
                <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500 leading-relaxed mb-6">
                  <p className="font-medium text-gray-700 mb-2">Terms & Conditions</p>
                  <ul className="space-y-1.5 list-disc pl-4">
                    <li>By signing this agreement, the buyer confirms they have reviewed all shed specifications listed above and agrees to purchase the described shed.</li>
                    <li>{isDeposit ? 'The deposit is non-refundable and reserves this shed exclusively for the buyer. The remaining balance is due at delivery.' : 'Payment in full is due upon signing. The shed will be scheduled for delivery after payment clears.'}</li>
                    <li>Delivery and setup will be coordinated by the dealer. Standard delivery is typically within 2-4 weeks.</li>
                    <li>The shed comes with the manufacturer warranty as specified above.</li>
                    <li>Site preparation (level ground, clear access) is the buyer's responsibility.</li>
                  </ul>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FiFileText className="w-4 h-4" />
                  Continue to Sign
                </button>
              </div>
            )}

            {/* Step 2: Sign */}
            {step === 2 && (
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Please sign below to confirm you have reviewed and agree to the purchase of:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="font-medium text-gray-900">{shed?.name}</p>
                  <p className="text-sm text-gray-500">{shed?.size} &middot; {shed?.shedType} &middot; {shed?.material}</p>
                  <p className="text-sm font-semibold text-gray-900 mt-2">
                    {isDeposit ? `Deposit: ${formatPrice(depositAmount)}` : `Total: ${formatPrice(shedPrice)}`}
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Signature</label>
                  <SignatureCanvas
                    onSignatureChange={setSignature}
                    width={500}
                    height={180}
                    label={customer?.name || 'Sign here'}
                  />
                </div>

                <label className="flex items-start gap-3 mb-6 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-gray-600">
                    I have reviewed the shed specifications above and agree to the purchase terms. I understand this is a binding agreement.
                  </span>
                </label>

                {error && (
                  <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2 mb-4">
                    <FiAlertCircle className="flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => { setStep(1); setError(''); }}
                    className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSign}
                    disabled={loading || !signature || !agreed}
                    className="flex-[2] bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Processing...</>
                    ) : (
                      <><FiLock className="w-4 h-4" /> Sign & Continue to Payment</>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Confirmed */}
            {step === 3 && (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiCheck className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Agreement Signed</h3>
                <p className="text-sm text-gray-500 mb-2">Invoice #{invNum}</p>
                <p className="text-sm text-gray-600 mb-6">
                  A copy of this agreement has been emailed to <strong>{customer?.email}</strong>
                  {dealer?.email && <> and the dealer at <strong>{dealer.email}</strong></>}.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-500">Shed</span>
                    <span className="font-medium">{shed?.name}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{isDeposit ? 'Deposit' : 'Total'}</span>
                    <span className="font-semibold text-emerald-600">{formatPrice(amount)}</span>
                  </div>
                </div>

                <button
                  onClick={handleProceedToPayment}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white font-medium py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FiLock className="w-4 h-4" />
                  Proceed to Payment
                </button>

                <p className="text-xs text-gray-400 mt-3">
                  You'll be redirected to Stripe's secure checkout to complete payment.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PurchaseAgreement;
